import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import {
  DataGridPremium,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
  isAutogeneratedRow,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarColumnsButton,
} from '@mui/x-data-grid-premium';
import { huHU } from '@mui/x-data-grid/locales';
import { datagridLangHunCustomized } from '../../../config/datagridLangHunCustomized';
import {
  Select,
  MenuItem,
  IconButton,
  ClickAwayListener,
  Modal,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  FormControl,
  Checkbox,
} from '@mui/material';
import { Delete, Save, KeyboardDoubleArrowDown, Menu as MenuIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useTheme } from '@mui/material/styles';

import useParam from '../../../context/ParamContext';
import useConfig from '../../../context/ConfigContext';
import { ButtonUI, ColorCircleUi, TrendIcon } from '../../Interface';
import biService from '../../../service/bi.service';
import { BiFieldType } from '../../../config/bi_report';
import FeedbackDialog from './FeedbackDialog';
import DrillSelectionPath from './DrillSelectionPath';
import { formatNumberByConfig, NumberFormatKind } from '../../../utils/numberFormatHelper';
import { getTableReportStyles } from './tableStyle';

const TableReport = ({
  report,
  height,
  setRefresh,
  linkedReportIdState,
  activeLevelState,
  filterValsState,
  reportStatesState,
}) => {
  const theme = useTheme();
  const arf = useGridApiRef();
  const { getParam, params } = useParam();
  const { config, getConfig } = useConfig();
  const apiRef = useGridApiRef();

  const { activeLevel, setActiveLevel } = activeLevelState;
  const { filterVals, setFilterVals } = filterValsState;
  const { linkedReportId, setLinkedReportId } = linkedReportIdState;
  const { reportStates, setReportStates } = reportStatesState;
  const tableRowHeight = 36;

  const [gridData, setGridData] = useState({ rows: [], rowCount: 0 });
  const [columns, setColumns] = useState([]);
  const [savedStates, setSavedStates] = useState({});
  const [selectedState, setSelectedState] = useState('');
  const [isSelectOpen, setSelectOpen] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [stateName, setStateName] = useState('');
  const [isOverwriteDialogOpen, setOverwriteDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [stateToDelete, setStateToDelete] = useState('');
  const [dialogData, setDialogData] = useState({});
  const [hasAppliedDefaultState, setHasAppliedDefaultState] = useState(false);
  const [isToolbarMenuOpen, setToolbarMenuOpen] = useState(false);

  const query = useMemo(
    () => ({
      filter: filterVals.filter,
      sorting: {
        sortModel: [],
      },
      pagination: {
        paginationModel: {
          page: 0,
        },
      },
    }),
    [report.id, filterVals]
  );

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      rowGrouping: {
        model: [],
        aggregation: {
          model: {},
        },
      },
      density: 'compact',
    },
  });

  const getTableData = useCallback(() => {
    biService.getReportData(report.id, activeLevel, query).then((data) => setGridData(data));
  }, [report.id, activeLevel, query]);

  const NumberField = ({ data, field }) => {
    const rawValue = data?.value ?? data.row?.[field?.field] ?? data?.formattedValue;
    const val = Number(rawValue);
    const formattedValue = formatNumberByConfig(val, {
      config,
      kind:
        field.type === BiFieldType.SUM
          ? NumberFormatKind.SUM
          : field.type === BiFieldType.PRICE || field.type === BiFieldType.CURRENCY
            ? NumberFormatKind.PRICE
            : field.type === BiFieldType.QUANTITY
              ? NumberFormatKind.QUANTITY
              : NumberFormatKind.DEFAULT,
    });
    const threshold = {
      min: field.thresholdMin ? parseFloat(field.thresholdMin) : null,
      max: field.thresholdMax ? parseFloat(field.thresholdMax) : null,
      arrow: field.thresholdArrow,
    };
    const biTableFeedback = JSON.parse(params?.BI_TABLE_FEEDBACK ?? '[]');
    const hasFeedback = biTableFeedback.includes(report.id) && !isAutogeneratedRow(data.row);

    return ((field.thresholdMin !== null && !isNaN(field.thresholdMin)) ||
      (field.thresholdMax !== null && !isNaN(field.thresholdMax))) &&
      !isNaN(val) ? (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '-3px' }}>
        <Box>{formattedValue}</Box>
        <TrendIcon
          rowData={data}
          field={field}
          threshold={threshold}
          hasFeedback={hasFeedback}
          setDialogData={setDialogData}
        />
      </Box>
    ) : !isNaN(val) ? (
      formattedValue
    ) : (
      data.row?.[field?.field]
    );
  };

  const onDrillClick = (field, value) => {
    const { biDrillingLink, biDrilling, id } = report;

    const maxLevel = biDrilling.length > 0 ? Math.max(...biDrilling.map((drill) => drill.level)) : null;
    let actLevel = activeLevel !== null ? activeLevel + 1 : 0;

    const newFilter = { ...filterVals };

    if (maxLevel + 1 === actLevel || (biDrillingLink && maxLevel === null)) {
      if (biDrillingLink) {
        setReportStates((prev) => {
          prev.push({
            reportId: id,
            linkedReportId: linkedReportId,
            filter: { filter: newFilter.filter.map((data) => data) },
            activeLevel,
          });
          return prev;
        });
        setLinkedReportId(biDrillingLink.linkedReport);

        newFilter.filter.push({ field, value });

        const updatedFilter = newFilter.filter.map((item) => {
          const newReportField = biDrillingLink.biDrillingLinkField.find(
            (linkField) => linkField.parentField === item.field
          );
          if (newReportField) {
            return { ...item, field: newReportField.childField };
          }
          return item;
        });

        setFilterVals({ filter: updatedFilter });
        setActiveLevel(null);
      } else {
        return;
      }
    } else {
      newFilter.filter.push({ field, value });
      if (!biDrilling.length > 0) {
        return;
      }
      setActiveLevel(actLevel);
      setFilterVals(newFilter);
    }
  };

  const generateColumns = () => {
    const col = [];

    for (const field of report.biField) {
      if (field.type !== BiFieldType.HIDDEN) {
        const column = {
          field: field?.field,
          headerName: field?.name,
          headerClassName: 'bg-tableHeaderColor',
          filterable: true,
          sortable: true,
          flex: 1,
          minWidth: 100,
        };

        switch (field.type) {
          case BiFieldType.DATE:
            column.type = 'date';
            column.valueGetter = (value) => (value ? new Date(value) : null);
            column.renderCell = (value) => {
              if (!isAutogeneratedRow(value.row)) {
                return value.row?.[field?.field] ? dayjs(value.row?.[field?.field]).format('YYYY.MM.DD') : '';
              } else {
                return value.formattedValue ? dayjs(value.formattedValue).format('YYYY.MM.DD') : '';
              }
            };

            break;
          case BiFieldType.DATETIME:
            column.type = 'dateTime';
            column.valueGetter = (value) => (value ? new Date(value) : null);
            column.renderCell = (value) => {
              if (!isAutogeneratedRow(value.row)) {
                return value.row?.[field?.field] ? dayjs(value.row?.[field?.field]).format('YYYY.MM.DD HH:mm') : '';
              } else {
                return value.formattedValue ? dayjs(value.formattedValue).format('YYYY.MM.DD HH:mm') : '';
              }
            };
            break;
          case BiFieldType.TIME:
            column.valueFormatter = (data) => (data ? dayjs(data).format('HH:mm') : '');
            break;
          case BiFieldType.NUMBER:
          case BiFieldType.CURRENCY:
          case BiFieldType.QUANTITY:
          case BiFieldType.PRICE:
          case BiFieldType.SUM:
            column.type = 'number';
            column.valueGetter = (data) => {
              if (data === null || data === undefined || data === '') {
                return null;
              }

              const parsedValue = Number(data);
              return Number.isFinite(parsedValue) ? parsedValue : null;
            };
            column.valueFormatter = (value) =>
              formatNumberByConfig(value, {
                config,
                kind:
                  field.type === BiFieldType.SUM
                    ? NumberFormatKind.SUM
                    : field.type === BiFieldType.PRICE || field.type === BiFieldType.CURRENCY
                      ? NumberFormatKind.PRICE
                      : field.type === BiFieldType.QUANTITY
                        ? NumberFormatKind.QUANTITY
                        : NumberFormatKind.DEFAULT,
              });
            column.renderCell = (data) => <NumberField data={data} field={field} />;
            break;
          case BiFieldType.COLOR:
            column.sortable = false;
            column.filterable = false;
            column.renderCell = (data) => {
              const colorValue = data.row?.[field?.field];
              return <ColorCircleUi color={colorValue} size={20} />;
            };
            break;
          default:
            break;
        }

        col.push(column);

        if (field.isGroup) {
          const drillColumn = {
            field: `__bi_drill_${field.field}`,
            headerName: `Fúrás`,
            headerClassName: 'bg-tableHeaderColor',
            filterable: false,
            sortable: false,
            flex: 0.8,
            minWidth: 80,
            renderCell: (data) => {
              if (!isAutogeneratedRow(data.row)) {
                const density = apiRef.current?.state?.density?.value || 'compact';
                const iconButtonSize = density === 'comfortable' ? 'medium' : 'small';
                const iconFontSize = density === 'compact' ? 'small' : 'medium';
                const iconButtonPadding = density === 'comfortable' ? 0.9 : density === 'standard' ? 0.6 : 0.2;
                const iconButtonDimension = density === 'comfortable' ? 32 : density === 'standard' ? 28 : 22;
                const iconButtonOffsetY = density === 'comfortable' ? -1 : density === 'standard' ? -2 : -3;

                return (
                  <IconButton
                    aria-label="save"
                    size={iconButtonSize}
                    onClick={() => {
                      onDrillClick(field.field, data.row[field.field]);
                    }}
                    sx={{
                      color: 'primary.main',
                      p: iconButtonPadding,
                      width: iconButtonDimension,
                      height: iconButtonDimension,
                      transform: `translateY(${iconButtonOffsetY}px)`,
                    }}
                  >
                    <KeyboardDoubleArrowDown fontSize={iconFontSize} />
                  </IconButton>
                );
              } else {
                return data.formattedValue;
              }
            },
          };
          col.push(drillColumn);
        }
      }
    }

    setColumns(col);
  };

  useEffect(() => {
    if (!Object.keys(params).length) {
      getParam();
    }
  }, []);

  useEffect(() => {
    if (!Object.keys(config ?? {}).length) {
      getConfig();
    }
  }, [config, getConfig]);

  useEffect(() => {
    if (Object.keys(report).length) {
      generateColumns();
    }
  }, [report, report?.biField]);

  useEffect(() => {
    const prevReport = reportStates[reportStates.length - 1];
    if (Object.keys(query).length > 0 && prevReport?.reportId !== report.id) {
      getTableData();
    }
  }, [query]);

  useEffect(() => {
    biService.getReportReportGridState(report.id).then((savedStateTable) => setSavedStates(savedStateTable));
    // const savedStates = JSON.parse(localStorage.getItem(`biGridStates_${report.id}`)) || {};
    // setSavedStates(savedStates);
  }, [report.id]);

  useEffect(() => {
    if (params) {
    }
  }, [params]);

  const handleSaveState = () => {
    setModalOpen(true);
  };

  const handleSaveStateConfirm = () => {
    if (savedStates[stateName]) {
      setModalOpen(false);
      setOverwriteDialogOpen(true);
    } else {
      saveState();
    }
  };

  const saveState = async () => {
    const rawCurrentState = apiRef.current.exportState();
    const {
      rows: _rows,
      pagination: _pagination,
      paginationModel: _paginationModel,
      rowCount: _rowCount,
      ...currentState
    } = rawCurrentState || {};

    const previousState = savedStates[stateName];
    const isDefault =
      previousState && typeof previousState === 'object' && 'isDefault' in previousState
        ? previousState.isDefault
        : false;

    const newSavedStates = {
      ...savedStates,
      [stateName]: {
        ...currentState,
        isDefault,
      },
    };

    setSavedStates(newSavedStates);
    // localStorage.setItem(`biGridStates_${report.id}`, JSON.stringify(newSavedStates));
    await biService.saveReportGridState(report.id, newSavedStates);
    setModalOpen(false);
    setOverwriteDialogOpen(false);
    setStateName('');
  };

  const handleConfirmOverwrite = () => {
    saveState();
  };

  const handleLoadState = useCallback(
    (stateName) => {
      const savedState = savedStates[stateName];
      if (savedState) {
        const {
          rows: _rows,
          isDefault: _isDefault,
          pagination: _pagination,
          paginationModel: _paginationModel,
          rowCount: _rowCount,
          ...gridState
        } = savedState || {};
        apiRef.current.restoreState(gridState);

        const currentPaginationModel = apiRef.current.state?.pagination?.paginationModel;
        const pageSize = currentPaginationModel?.pageSize || 25;

        apiRef.current.setPaginationModel({ page: 0, pageSize });
      }
    },
    [savedStates, apiRef]
  );

  const setDefaultState = async (stateName) => {
    const updatedStates = Object.entries(savedStates).reduce((acc, [name, stateValue]) => {
      const baseState = stateValue && typeof stateValue === 'object' ? stateValue : {};
      const {
        rows: _rows,
        pagination: _pagination,
        paginationModel: _paginationModel,
        rowCount: _rowCount,
        ...cleanState
      } = baseState;
      acc[name] = { ...cleanState, isDefault: stateName ? name === stateName : false };
      return acc;
    }, {});
    setSavedStates(updatedStates);
    await biService.saveReportGridState(report.id, updatedStates);
  };

  const handleDeleteState = (stateName) => {
    setStateToDelete(stateName);
    setDeleteDialogOpen(true);
  };

  const hasDrillSelections = useMemo(() => (filterVals?.filter ?? []).some((item) => !item?.isDefault), [filterVals]);

  const handleResetDrill = useCallback(() => {
    setFilterVals((prev) => {
      const defaultFilters = (prev?.filter ?? []).filter((item) => item?.isDefault);
      return { ...prev, filter: defaultFilters };
    });
    setActiveLevel(null);
  }, [setFilterVals, setActiveLevel]);

  const handleSelectDrillLevel = useCallback(
    (targetIndex) => {
      setFilterVals((prev) => {
        const defaultFilters = (prev?.filter ?? []).filter((item) => item?.isDefault);
        const drillFilters = (prev?.filter ?? []).filter((item) => !item?.isDefault);
        const nextDrillFilters = drillFilters.slice(0, targetIndex + 1);
        return { ...prev, filter: [...defaultFilters, ...nextDrillFilters] };
      });
      setActiveLevel((prev) => (prev === null ? null : targetIndex));
    },
    [setFilterVals, setActiveLevel]
  );

  const confirmDeleteState = async () => {
    const newSavedStates = { ...savedStates };
    delete newSavedStates[stateToDelete];
    setSavedStates(newSavedStates);
    // localStorage.setItem(`biGridStates_${report.id}`, JSON.stringify(newSavedStates));
    await biService.saveReportGridState(report.id, newSavedStates);
    setDeleteDialogOpen(false);
    setStateToDelete('');
    if (selectedState === stateToDelete) {
      setSelectedState('');
    }
  };

  useEffect(() => {
    if (selectedState) {
      handleLoadState(selectedState);
    }
  }, [selectedState, handleLoadState]);

  useEffect(() => {
    if (hasAppliedDefaultState) return;
    if (!savedStates || Object.keys(savedStates).length === 0) return;

    const defaultEntry = Object.entries(savedStates).find(([, stateValue]) => stateValue?.isDefault);
    if (defaultEntry) {
      const [defaultName] = defaultEntry;
      setSelectedState(defaultName);
    }
    setHasAppliedDefaultState(true);
  }, [savedStates, hasAppliedDefaultState, setSelectedState]);

  const CustomToolbar = () => {
    const exportFileName = `${report?.name || 'report'} ${dayjs().format('YYMMDD_HHmm')}`;

    return (
      <GridToolbarContainer sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            aria-label="toolbar-actions"
            size="medium"
            onClick={() => setToolbarMenuOpen((prev) => !prev)}
            sx={{ color: 'primary.main' }}
          >
            <MenuIcon />
          </IconButton>
          <GridToolbarFilterButton />
          {isToolbarMenuOpen && (
            <ClickAwayListener onClickAway={() => setToolbarMenuOpen(false)} disableReactTree>
              <Box
                sx={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  zIndex: 20,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                  boxShadow: theme.shadows[4],
                }}
              >
                <Box onClick={() => setToolbarMenuOpen(false)}>
                  <GridToolbarColumnsButton />
                </Box>
                <GridToolbarDensitySelector />
                <GridToolbarExport
                  csvOptions={{ fileName: exportFileName }}
                  excelOptions={{ fileName: exportFileName }}
                  printOptions={{ fileName: exportFileName }}
                />
              </Box>
            </ClickAwayListener>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl variant="standard" sx={{ width: '220px' }}>
            <Select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              displayEmpty
              onOpen={() => setSelectOpen(true)}
              onClose={() => setSelectOpen(false)}
              onClick={() => setSelectOpen(true)}
              sx={{
                color: 'primary.main',
                '& .MuiSelect-icon': {
                  color: 'primary.main',
                },
              }}
            >
              <MenuItem value="" disabled>
                Mentett állapot kiválasztása
              </MenuItem>
              {Object.keys(savedStates)
                .sort()
                .map((state) => (
                  <MenuItem key={state} value={state}>
                    <Checkbox
                      size="small"
                      checked={!!savedStates[state]?.isDefault}
                      onChange={(e) => {
                        e.stopPropagation();
                        if (e.target.checked) {
                          setDefaultState(state);
                        } else {
                          if (savedStates[state]?.isDefault) {
                            setDefaultState(null);
                          }
                        }
                      }}
                      sx={{ p: 0, mr: 1 }}
                    />
                    {state}
                    {isSelectOpen && (
                      <IconButton
                        aria-label="delete"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteState(state);
                        }}
                        sx={{
                          marginLeft: 'auto',
                          color: 'primary.main',
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <IconButton
            aria-label="save"
            size="medium"
            onClick={handleSaveState}
            sx={{
              color: 'primary.main',
            }}
          >
            <Save />
          </IconButton>
        </Box>
      </GridToolbarContainer>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-2 p-4">
      <div className="col-span-1 flex items-center justify-center text-labelColor text-xl w-full">
        <div className="flex flex-col items-center w-full">
          <button
            type="button"
            onClick={handleResetDrill}
            disabled={!hasDrillSelections}
            className={`${hasDrillSelections ? 'cursor-pointer hover:underline' : 'cursor-default'}`}
          >
            {report.name}
          </button>
          <DrillSelectionPath filterVals={filterVals} onSelectLevel={handleSelectDrillLevel} />
        </div>
      </div>

      <div className="tableReport">
        <DataGridPremium
          initialState={initialState}
          apiRef={apiRef}
          rows={gridData.rows}
          columns={columns}
          groupingColDef={{
            hideable: false,
          }}
          pagination
          rowHeight={tableRowHeight}
          sx={getTableReportStyles(theme, height, tableRowHeight)}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            columnsPanel: {
              sx: {
                '& .MuiDataGrid-panelFooter button:first-child': {
                  display: 'none',
                },
              },
            },
            pagination: {
              showFirstButton: true,
              showLastButton: true,
            },
          }}
          localeText={{ ...huHU.components.MuiDataGrid.defaultProps.localeText, ...datagridLangHunCustomized }}
          disableSelectionOnClick
          headerFilters
        />
      </div>
      {/* Save State Modal */}
      <Modal open={isModalOpen} onClose={() => setModalOpen(false)}>
        <div
          className="modal-container"
          style={{
            padding: 20,
            backgroundColor: 'white',
            margin: 'auto',
            top: '20%',
            position: 'relative',
            width: 300,
          }}
        >
          <h2>Jelenlegi állapot mentése</h2>
          <TextField label="State Name" value={stateName} onChange={(e) => setStateName(e.target.value)} fullWidth />
          <Button variant="contained" onClick={handleSaveStateConfirm} disabled={!stateName} style={{ marginTop: 10 }}>
            Mentés
          </Button>
        </div>
      </Modal>

      {/* Overwrite Confirmation Dialog */}
      <Dialog open={isOverwriteDialogOpen} onClose={() => setOverwriteDialogOpen(false)}>
        <DialogTitle>Felülírás megerősítése</DialogTitle>
        <DialogContent>
          <DialogContentText>Már létezik egy állapot "{stateName}" névvel. Szeretné felülírni?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverwriteDialogOpen(false)}>Mégsem</Button>
          <Button onClick={handleConfirmOverwrite} color="primary" variant="contained">
            Felülír
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Törlés megerősítése</DialogTitle>
        <DialogContent>
          <DialogContentText>Biztos benne, hogy törölni szeretné a(z) "{stateToDelete}" állapotot?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <ButtonUI text={'Törlés'} className="bg-danger" type="button" onClick={confirmDeleteState} />
        </DialogActions>
      </Dialog>

      <FeedbackDialog
        show={Object.keys(dialogData).length > 0}
        onHide={() => {
          setDialogData({});
        }}
        selectedValues={dialogData}
        biReportId={report.id}
        setRefresh={setRefresh}
      />
    </div>
  );
};

export default TableReport;
